import { io } from 'socket.io-client';
import { mainApi as api } from './emptyApi';
import { lotteryAutoplayGameClose, setGameLostConnection } from '@features/LotteryGame';
import { UserDto, PayoutDto, CreatePayoutBodyDto } from './restApi';
import { removeSocket, setSocket } from '../../../utils/gameSockets';
import { connectGame, disconnectGame, setLoading } from '../slices/gameSlice';
import { balanceOnMessage } from '../../../features/Balance/api/balanceOnMessage';
import { lotteryOnMessage } from '../../../features/LotteryGame/api/lotteryOnMessage';

export type { UserDto, PayoutDto, CreatePayoutBodyDto };

const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    authControllerWhoami: build.query<
      AuthControllerWhoamiApiResponse,
      AuthControllerWhoamiApiArg
    >({
      query: () => ({ url: `/api/auth/whoami` }),
      providesTags: ['Balance'],
    }),
    virtualBalanceControllerIncreaseUserBalance: build.mutation<
      VirtualBalanceControllerIncreaseUserBalanceApiResponse,
      VirtualBalanceControllerIncreaseUserBalanceApiArg
    >({
      query: (queryArg) => ({
        url: `/api/virtual-balance/increase/${queryArg.userId}/${queryArg.amount}`,
        method: 'POST',
      }),
      invalidatesTags: ['Balance'],
    }),
    incomeControllerTakePartnerIncome: build.mutation<
      IncomeControllerTakePartnerIncomeApiResponse,
      IncomeControllerTakePartnerIncomeApiArg
    >({
      query: () => ({ url: `/api/income/take-partner-income`, method: 'POST' }),
      invalidatesTags: ['Balance'],
    }),
    incomeControllerTakeReferralIncome: build.mutation<
      IncomeControllerTakeReferralIncomeApiResponse,
      IncomeControllerTakeReferralIncomeApiArg
    >({
      query: () => ({ url: `/api/income/take-referral-income`, method: 'POST' }),
      invalidatesTags: ['Balance'],
    }),
    incomeControllerTakeJackpotIncome: build.mutation<
      IncomeControllerTakeJackpotIncomeApiResponse,
      IncomeControllerTakeJackpotIncomeApiArg
    >({
      query: () => ({ url: `/api/income/take-jackpot-income`, method: 'POST' }),
      invalidatesTags: ['Balance'],
    }),
    gameControllerStartSession: build.mutation<
      GameControllerStartSessionApiResponse,
      GameControllerStartSessionApiArg
    >({
      query: () => ({ url: `/api/game/start-session`, method: 'POST' }),
      onCacheEntryAdded: async (
        _,
        { cacheDataLoaded, getCacheEntry, cacheEntryRemoved, dispatch },
      ) => {
        try {
          dispatch(setLoading(true));
          await cacheDataLoaded;
          const record = getCacheEntry();
          if (record.isSuccess) {
            const { gameServerUrl, token } = record.data;
            const socket = io(gameServerUrl, {
              transports: ['websocket'],
              auth: {
                token,
              },
            });
            lotteryOnMessage(token)(dispatch, socket);
            balanceOnMessage(dispatch, socket);
            socket.on('connect', () => {
              console.log('connected');
              setSocket(token, socket);
              dispatch(connectGame({ wss: gameServerUrl, token }));
            });
            socket.on('disconnect', (reason) => {
              console.log('disconnected', reason);
              removeSocket(token);
              dispatch(disconnectGame());
              dispatch(setGameLostConnection());
              dispatch(lotteryAutoplayGameClose());
            });
            await cacheEntryRemoved;
            socket.close();
          }
        } catch (e) {
          dispatch(setLoading(false));
          console.log(e);
        }
      },
    }),
    photoControllerGetUserPhoto: build.query<string | undefined, void>({
      query: () => ({
        url: `/api/photo`,
        responseHandler: async (response) =>
          window.URL.createObjectURL(await response.blob()),
      }),
    }),
    payoutControllerCreatePayout: build.mutation<
      PayoutControllerCreatePayoutApiResponse,
      PayoutControllerCreatePayoutApiArg
    >({
      query: (queryArg) => ({
        url: `/api/payout`,
        method: 'POST',
        body: queryArg.createPayoutBodyDto,
      }),
      invalidatesTags: ['Balance'],
    }),
    photoControllerGetReferralUserPhoto: build.query<
      string | undefined,
      PhotoControllerGetReferralUserPhotoApiArg
    >({
      query: (queryArg) => ({
        url: `/api/photo/referral-user/${queryArg.userDisplayNumber}`,
        responseHandler: async (response) =>
          window.URL.createObjectURL(await response.blob()),
      }),
    }),
  }),
  overrideExisting: false,
});

export type AuthControllerWhoamiApiResponse = /** status 200  */ UserDto;
export type AuthControllerWhoamiApiArg = void;
export type BalanceControllerAddBalanceApiResponse = /** status 200  */ {
  balance?: number;
};
export type UserJackpotEarningsHistory = {
  date: string;
  amount: string;
};
export type UserPartnerEarningsHistory = {
  date: string;
  amount: string;
};
export type VirtualBalanceControllerIncreaseUserBalanceApiResponse =
  /** status 200  */ boolean;
export type VirtualBalanceControllerIncreaseUserBalanceApiArg = {
  userId: string;
  amount: string;
};
export type IncomeControllerTakePartnerIncomeApiResponse = /** status 200  */ boolean;
export type IncomeControllerTakePartnerIncomeApiArg = void;
export type IncomeControllerTakeReferralIncomeApiResponse = /** status 200  */ boolean;
export type IncomeControllerTakeReferralIncomeApiArg = void;
export type IncomeControllerTakeJackpotIncomeApiResponse = /** status 200  */ boolean;
export type IncomeControllerTakeJackpotIncomeApiArg = void;
export type BalanceControllerAddBalanceApiArg = {
  changeBalanceBodyDto: ChangeBalanceBodyDto;
};
export type GameControllerStartSessionApiResponse = /** status 200  */ {
  gameServerUrl: string;
  token: string;
};
export type GameControllerStartSessionApiArg = void;
export type ChangeBalanceBodyDto = {
  amount: number;
};
export type PayoutControllerCreatePayoutApiResponse = /** status 201  */ PayoutDto;
export type PayoutControllerCreatePayoutApiArg = {
  createPayoutBodyDto: CreatePayoutBodyDto;
};
export type PhotoControllerGetReferralUserPhotoApiArg = {
  userDisplayNumber: number;
};
export const {
  useAuthControllerWhoamiQuery,
  useVirtualBalanceControllerIncreaseUserBalanceMutation,
  useGameControllerStartSessionMutation,
  usePhotoControllerGetUserPhotoQuery,
  usePayoutControllerCreatePayoutMutation,
  usePhotoControllerGetReferralUserPhotoQuery,
  useIncomeControllerTakePartnerIncomeMutation,
  useIncomeControllerTakeReferralIncomeMutation,
  useIncomeControllerTakeJackpotIncomeMutation,
} = injectedRtkApi;
